import {
  call,
  put,
  race,
  select,
  spawn,
  take,
  takeLatest,
} from 'redux-saga/effects';
import ActionTypes from '../../../../Data/ActionTypes';
import FetchStatus from '../../../../Data/FetchStatus';
import JourneyOutcome from '../../../../Data/JourneyOutcome';
import JourneyOutcomeDetail from '../../../../Data/JourneyOutcomeDetail';
import { selectPostSubmissionAnswers } from '../../../../Helpers/strategyCollection.helpers';
import { incrementMetric } from '../../../../Services/Datadog';
import {
  setSubscriptionMatchStatus,
  setSubscriptionMatched,
} from '../../slices/clientApiJourney';
import { getErrorTypeTag } from '../clientApiError.helpers';
import reportJourneyOutcomeOnce from '../reportJourneyOutcomeOnce';
import { captureWithSubmissionId } from '../SubmitClientApiBooking/captureWithSubmissionId';
import isSelectedSubscriptionMatchingQuery from './isSelectedSubscriptionMatchingQuery';

// The gateway accepts a string or a list of strings per answer; step responses
// may carry numbers or booleans.
const toAnswerValue = (answer) =>
  Array.isArray(answer) ? answer.map(String) : String(answer);

const toAnswerInputs = (answers) =>
  Object.entries(answers).map(([questionId, answer]) => ({
    questionId,
    answerValue: toAnswerValue(answer),
  }));

// Swallows its own errors so a failed outcome send cannot break the check.
function* reportSubscriptionNoLongerMatched(submissionId) {
  try {
    yield call(reportJourneyOutcomeOnce, {
      outcome: JourneyOutcome.GOAL_UNMET,
      outcomeDetail: JourneyOutcomeDetail.SUBSCRIPTION_NO_LONGER_MATCHED,
    });
  } catch (error) {
    incrementMetric({
      name: 'clientApiSubscriptionMatchOutcomeError',
      tags: { type: getErrorTypeTag(error) },
    });
    captureWithSubmissionId({ error, submissionId });
  }
}

/**
 * Asks the gateway whether the journey's subscription still matches the lead
 * once every post-submission answer is applied. A false result or a failed
 * check both end the journey (fail closed).
 */
export function* checkSubscriptionMatchWorker() {
  const submissionId = yield select((state) => state.submission?.id);
  const token = yield select((state) => state.submission?.token);
  const answers = yield select(selectPostSubmissionAnswers);

  yield put(
    setSubscriptionMatchStatus({
      subscriptionMatchStatus: FetchStatus.IN_PROGRESS,
    })
  );

  let isMatched;
  try {
    isMatched = yield call(isSelectedSubscriptionMatchingQuery, {
      submissionId,
      token,
      answers: toAnswerInputs(answers),
    });

    incrementMetric({
      name: 'clientApiSubscriptionMatchResult',
      tags: { result: String(isMatched) },
    });

    yield put(setSubscriptionMatched({ isSubscriptionMatched: isMatched }));
    yield put(
      setSubscriptionMatchStatus({
        subscriptionMatchStatus: FetchStatus.COMPLETED,
      })
    );
  } catch (error) {
    incrementMetric({
      name: 'clientApiSubscriptionMatchError',
      tags: { type: getErrorTypeTag(error) },
    });
    captureWithSubmissionId({ error, submissionId });
    yield put(
      setSubscriptionMatchStatus({
        subscriptionMatchStatus: FetchStatus.FAILED,
      })
    );
  }

  if (isMatched === true) return;

  // Detached from the cancel race: once the send has started it must finish
  // and set the once-only flag, or a later outcome could be sent as well.
  yield spawn(reportSubscriptionNoLongerMatched, submissionId);
}

// Leaving the review screen cancels the check, so a late result cannot report
// an outcome for a journey the customer is still in.
export function* checkSubscriptionMatchUntilCancelled() {
  yield race({
    task: call(checkSubscriptionMatchWorker),
    cancelled: take(ActionTypes.CANCEL_SUBSCRIPTION_MATCH_CHECK),
  });
}

export default function* checkSubscriptionMatchWatcher() {
  yield takeLatest(
    ActionTypes.CHECK_SUBSCRIPTION_MATCH,
    checkSubscriptionMatchUntilCancelled
  );
}
